// Chakra imports
import { Badge, Box, Flex, IconButton, SimpleGrid, Text } from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';

import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaCheck, FaClock, FaEye, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Column } from 'react-table';
import { findAllMovements } from 'services/api.service';
import { MovementsType, MovementsTypeDescription } from 'types/Movement';
import { maskCurrency } from 'utils/number';

export enum MovementsStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
}

export const StatusRender = (status: MovementsStatus) => {
  if (status === MovementsStatus.PENDING)
    return (
      <Badge colorScheme="orange">
        <FaClock />
      </Badge>
    );
  if (status === MovementsStatus.APPROVED)
    return (
      <Badge colorScheme="green">
        <FaCheck />
      </Badge>
    );
  if (status === MovementsStatus.REFUNDED)
    return (
      <Badge colorScheme="orange">
        <FaTimes />
      </Badge>
    );
  if (status === MovementsStatus.REJECTED || status === MovementsStatus.CANCELED)
    return (
      <Badge colorScheme="red">
        <FaTimes />
      </Badge>
    );
};
export default function Orders() {
  const navigate = useNavigate();
  const columns = useMemo(
    () =>
      [
        {
          Header: 'Tipo',
          filter: true,
          values: Object.values(MovementsType).map((type) => ({
            value: type,
            label: MovementsTypeDescription[type],
          })),
          accessor: 'type',
          Cell: ({ value }) => {
            return (
              <Text
                noOfLines={1}
                fontSize="sm"
              >
                {MovementsTypeDescription[value]}
              </Text>
            );
          },
        },
        {
          Header: 'Status',
          accessor: 'status',
          filter: true,
          values: [
            {
              value: MovementsStatus.PENDING,
              label: 'Pendente',
            },
            {
              value: MovementsStatus.APPROVED,
              label: 'Aprovado',
            },
            {
              value: MovementsStatus.REJECTED,
              label: 'Rejeitado',
            },
            {
              value: MovementsStatus.CANCELED,
              label: 'Cancelado',
            },
          ],
          Cell: ({ value }: { value: MovementsStatus }) => {
            return StatusRender(value);
          },
        },
        {
          Header: 'Bruto',
          accessor: 'amount',
          align: 'right',
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },

        {
          Header: 'Líquido',
          align: 'right',
          accessor: 'amountLiquid',
          Cell: ({ value }) => {
            return maskCurrency(value);
          },
        },
        {
          Header: 'Parcelas',
          align: 'right',
          accessor: 'installmentCount',
          Cell: ({ value }) => {
            return value === 1 ? 'À vista' : `${value}x`;
          },
        },
        {
          accessor: 'orderAt',
          Header: 'Data',
          Cell: ({ value }) => {
            return DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss');
          },
        },

        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: any) => (
    <>
      <Flex
        align="center"
        justify="flex-start"
        w="100%"
        display={{ base: 'none', md: 'flex' }}
      >
        <IconButton
          aria-label="Apertar"
          size="sm"
          variant="outline"
          icon={<FaEye />}
          onClick={() => navigate(`/access/movement/${row.id}`)}
        />
      </Flex>
      <Flex
        align="center"
        justify="flex-start"
        w="100%"
        display={{ base: 'flex', md: 'none' }}
      >
        <CustomButton
          variant="solid"
          colorScheme="brand"
          onClick={() => navigate(`/access/movement/${row.id}`)}
        >
          Acessar
        </CustomButton>
      </Flex>
    </>
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['findAllMovements'] });
  };

  // Chakra Color Mode
  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        <SimpleGrid
          mb="20px"
          columns={{ sm: 1 }}
          spacing={{ base: '20px', xl: '20px' }}
        >
          <TableComponent
            columnsData={columns}
            filterable={['customerId', 'maquinetaId', 'createdAt', 'promoterId', 'paymentType', 'installments', 'flagCard']}
            queryFn={findAllMovements}
            exportPdf={true}
            exportCsv={true}
            queryKey="findAllMovements"
            actions={actions}
            label="Movimentações de vendas"
          />
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
